import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  InputAdornment,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core'
import { ReactComponent as EquipmentSvg } from '../../assets/equipment-type.svg'
import {
  InsurancePurchaseContextStore,
  INSURANCE_CONTEXT_ACTIONS,
} from '../../context/InsuracePurchaseContext'
import { HullItemSelectView } from './HullItemSelectView'
import './HullAddForm.css'
import { EquipmentType } from '../../models/hull-models/EuipmentTypes'
import FlexView from 'react-flexview/lib'
import {
  FirebaseService,
  FirebaseConfigKey,
} from '../../services/FirebaseService'
import { ImageDropzone } from '../Core/Dropzone/ImageDropzone'
import NewFillButton from '../Core/NewFillButton'
import { HullApi } from '../../api/hull.api'
import {
  awaitWithLoading,
  isUK,
  billingPeriodPretty,
} from '../../services/utils'
import {
  EventEmitterService,
  EventKey,
} from '../../services/EventEmitterService'
import { Action } from '../../models/Action'
import { useLocation, useParams } from 'react-router-dom'
import {
  PolicyHullExtensionContextStore,
  ACTIONS,
} from '../../context/PolicyHullExtensionContext'
import {
  COUNTRIES,
  HULL_MAX_LENGTH_SERIAL_NUMBER,
  HULL_MIN_LENGTH_SERIAL_NUMBER,
  UK_HULL_MIN_LENGTH_SERIAL_NUMBER,
} from '../../Constants'
import { COVERAGE_TYPES } from '../../models/insurance-models/CoverageType'
import { SessionService } from '../../services/SessionService'
import { OptionalTag } from '../Core/OptionalTag'
import HelpIcon from '@material-ui/icons/Help'
import LightTooltip from '../Core/LightTooltip'
import { HomeBuiltPopover } from '../Core/Popover/HomeBuiltPopover'
import { InsuranceBillingPeriod } from '../../models/insurance-models/InsuranceBillingPeriodModel'
import { RequesetSupportTag } from '../Core/RequestSupportTag'

const EQUIPMENT_TYPE_DEFAULT = 'equipment'
const DRONE_WEIGHT_TYPE_DEFAULT = 'noweight'

export const HullAddForm = (props) => {
  const { register, watch, trigger, setValue, errors, control, getValues } =
    useForm(
      props.editMode
        ? { defaultValues: props.defaultValues, mode: 'onChange' }
        : {
            mode: 'onChange',
            defaultValues: {
              type: props.openEquipmentFirst ? EQUIPMENT_TYPE_DEFAULT : 'drone',
              is_home_built: false,
            },
          }
    )
  const {
    register: registerEquipment,
    trigger: triggerEquipment,
    errors: errorsEquipment,
    getValues: getValuesEquipment,
  } = useForm({
    defaultValues: props.editMode ? props.defaultValues : { quantity: 1 },
    mode: 'onChange',
  })
  const location = useLocation()
  const insurancePurchaseContext = useContext(InsurancePurchaseContextStore)
  const policyExtensionContext = useContext(PolicyHullExtensionContextStore)

  const isPolicyManagementMode = () =>
    location.pathname.includes('policy-management')
  const context = isPolicyManagementMode()
    ? policyExtensionContext
    : insurancePurchaseContext
  const [hasImage, setHasImage] = useState(
    props.defaultValues && !!props.defaultValues.drone_image_id
  )
  const watchType = watch(
    'type',
    props.editMode
      ? props.defaultValues.type
      : props.openEquipmentFirst
        ? EQUIPMENT_TYPE_DEFAULT
        : 'drone'
  )
  let { insuranceId } = useParams()
  const watchDroneValue = watch('value')
  const homeBuiltWatch = watch(
    'is_home_built',
    props.editMode ? props.defaultValues.is_home_built : false
  )
  let insuranceType = isPolicyManagementMode()
    ? props.insuranceType
    : insurancePurchaseContext.selectedOption.billingPeriod
  const [showImageRequired, setShowImageRequired] = useState(false)

  const maximumDroneValue = SessionService.isAdmin()
    ? Number.MAX_SAFE_INTEGER
    : SessionService.isBroker()
      ? FirebaseService.getNumber(
          FirebaseConfigKey.MAXIMUM_DRONE_VALUE_FOR_BROKER
        )
      : FirebaseService.getNumber(FirebaseConfigKey.MAXIMUM_DRONE_VALUE)
  const maximumEquipmentValue = SessionService.isAdmin()
    ? Number.MAX_SAFE_INTEGER
    : SessionService.isBroker()
      ? FirebaseService.getNumber(
          FirebaseConfigKey.MAXIMUM_EQUIPMENT_VALUE_FOR_BROKER
        )
      : FirebaseService.getNumber(FirebaseConfigKey.MAXIMUM_EQUIPMENT_VALUE)
  const isDroneForm = () => watchType == 'drone'
  const changeDroneImage = () => setHasImage(false)

  const onDroneImageDrop = async (files) => {
    let res = await awaitWithLoading(HullApi.uploadDroneImage(files[0]))
    if (res.drone_image_id) {
      setValue('drone_image_id', res.drone_image_id)
      setHasImage(true)
      setShowImageRequired(false)
    } else {
      EventEmitterService.dispatch(EventKey.ShowError, res)
    }
  }

  const isBrokerWithNonMandatoryImageUpload = () => {
    return (
      SessionService.isBroker() &&
      !FirebaseService.getBoolean(
        FirebaseConfigKey.DRONE_IMAGE_REQUIRED_FOR_BROKER
      )
    )
  }

  const isImageRequired = (drone) => {
    // If the drone already has an image
    if (hasImage) return false
    if (insuranceType == InsuranceBillingPeriod.YEAR) {
      return false
    }
    // If the drone value is greater than minimum and the broker have to upload drone pictures
    else if (
      drone &&
      drone.value >
        FirebaseService.getNumber(
          FirebaseConfigKey.MINIMUM_HULL_VALUE_FOR_IMAGE_MANDATORY
        ) &&
      !isBrokerWithNonMandatoryImageUpload()
    )
      return true
    // If editing a drone in PolicyManagement then image is required
    else if (
      isPolicyManagementMode() &&
      !props.editMode &&
      !isBrokerWithNonMandatoryImageUpload()
    )
      return true
    else return false
  }

  const onSubmit = async () => {
    if (isDroneForm()) {
      let valid = await trigger()
      if (valid) {
        if (isImageRequired(getValues())) {
          setShowImageRequired(true)
          return
        } else {
          setShowImageRequired(false)
        }

        if (props.editMode) {
          if (props.editingInsuredHull)
            context.editInsuredDrone(getValues(), props.editIndex)
          else context.editDrone(getValues(), props.editIndex)
        } else {
          context.addDrone(getValues())
        }
      }
    } else {
      let validEquipmentDetails = await triggerEquipment()
      let validType = await trigger('type')
      if (validType && validEquipmentDetails) {
        let eq = getValuesEquipment()
        eq.type = getValues().type
        if (props.editMode) {
          if (props.editingInsuredHull)
            context.editInsuredEquipment(eq, props.editIndex)
          else context.editEquipment(eq, props.editIndex)
        } else {
          context.addEquipment(eq)
        }
      }
    }
  }

  const isHomeBuiltChange = (event) => {
    setValue('is_home_built', event.target.value == 'true' ? true : false)
  }

  useEffect(() => {
    register({ name: 'drone_image_id' })
    register({ name: 'is_home_built' })
  }, [register])

  return (
    <React.Fragment>
      {!isPolicyManagementMode() && (
        <div className="text-title blue bold textcenter title-hull-popup">
          Add the {isDroneForm() ? 'drone' : 'equipment'} you would like to
          insure
        </div>
      )}

      <Controller
        control={control}
        rules={{
          validate: (x) => x && x != 'type' && x != EQUIPMENT_TYPE_DEFAULT,
        }}
        name="type"
        as={
          <Select
            style={{ display: isDroneForm() ? 'none' : undefined }}
            fullWidth>
            {isDroneForm() && (
              <MenuItem value={'drone'}>
                <HullItemSelectView type={new EquipmentType('Drone')} />
              </MenuItem>
            )}
            <MenuItem value={EQUIPMENT_TYPE_DEFAULT} disabled>
              {' '}
              <FlexView vAlignContent={'bottom'}>
                <EquipmentSvg className="medium-icon" />
                <div className="text-body lightgrayblue margin-left-6">
                  Select equipment
                </div>
              </FlexView>
            </MenuItem>
            {context.equipmentTypes.map((type, index) => {
              return (
                <MenuItem key={index} value={type.name}>
                  {' '}
                  <HullItemSelectView type={type} />{' '}
                </MenuItem>
              )
            })}
          </Select>
        }
      />
      {!!errors.type && (
        <FormHelperText error={!!errors.type}>
          Must Select Item Type
        </FormHelperText>
      )}

      <FlexView column className="margin-top-medium">
        {isDroneForm() && (
          <React.Fragment>
            <TextField
              variant="outlined"
              label="Make"
              name="make"
              inputRef={register({ required: true })}
              helperText={errors.make ? 'Make is required' : 'e.g: DJI'}
              error={!!errors.make}
            />
            <TextField
              variant="outlined"
              className="margin-top-small"
              label="Model"
              name="model"
              inputRef={register({ required: true })}
              helperText={
                errors.model ? 'Model is required' : 'e.g: Phantom 4 pro'
              }
              error={!!errors.model}
            />
            <Grid
              className="mar
                            gin-top-small"
              container
              spacing={2}>
              <Grid item xs>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Serial Number"
                  name="serial"
                  inputRef={register({
                    required: true,
                    minLength: isUK()
                      ? UK_HULL_MIN_LENGTH_SERIAL_NUMBER
                      : HULL_MIN_LENGTH_SERIAL_NUMBER,
                    maxLength: HULL_MAX_LENGTH_SERIAL_NUMBER,
                  })}
                  helperText={
                    errors.serial &&
                    `Serial must be between ${
                      isUK()
                        ? UK_HULL_MIN_LENGTH_SERIAL_NUMBER
                        : HULL_MIN_LENGTH_SERIAL_NUMBER
                    } - ${HULL_MAX_LENGTH_SERIAL_NUMBER} characters`
                  }
                  error={!!errors.serial}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  disabled={
                    isPolicyManagementMode() &&
                    props.editMode &&
                    props.editingInsuredHull
                  }
                  variant="outlined"
                  label="Value"
                  type="number"
                  name="value"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {FirebaseService.getValue(
                          FirebaseConfigKey.CURRENCY_SYMBOL
                        )}
                      </InputAdornment>
                    ),
                  }}
                  inputRef={register({
                    required: true,
                    min: FirebaseService.getNumber(
                      FirebaseConfigKey.MINIMUN_DRONE_VALUE
                    ),
                    max: maximumDroneValue,
                  })}
                  helperText={
                    errors.value &&
                    `Value must be between ${FirebaseService.getValue(
                      FirebaseConfigKey.CURRENCY_SYMBOL
                    )}${FirebaseService.getNumber(
                      FirebaseConfigKey.MINIMUN_DRONE_VALUE
                    )} - ${FirebaseService.getValue(
                      FirebaseConfigKey.CURRENCY_SYMBOL
                    )}${maximumDroneValue.toLocaleString()}`
                  }
                  error={!!errors.value}
                />
              </Grid>
            </Grid>
            {!!errors.value && (
              <RequesetSupportTag
                tag={'hull_drones_value'}
                text={'For higher drone value'}
              />
            )}

            {FirebaseService.getValue(FirebaseConfigKey.COUNTRY) ==
              COUNTRIES.UK && (
              <>
                <Controller
                  control={control}
                  rules={{
                    validate: (x) => x && x != DRONE_WEIGHT_TYPE_DEFAULT,
                    required: true,
                  }}
                  name="weight"
                  defaultValue={
                    getValues()['weight'] || DRONE_WEIGHT_TYPE_DEFAULT
                  }
                  as={
                    <Select
                      error={!!errors.weight}
                      variant="outlined"
                      className="margin-top-small">
                      <MenuItem disabled value={DRONE_WEIGHT_TYPE_DEFAULT}>
                        <div className="placeholder-text">Weight (KG)</div>
                      </MenuItem>
                      {FirebaseService.getArray(
                        FirebaseConfigKey.DRONE_TAKEOFF_WEIGHTS
                      ).map((w) => (
                        <MenuItem key={w} value={w}>
                          {w}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
                <FormHelperText error={!!errors.weight}>
                  Select Weight (KG)
                </FormHelperText>
              </>
            )}

            <ImageDropzone
              changeImage={changeDroneImage}
              hasImage={hasImage}
              onDrop={onDroneImageDrop}
              isImageMandatory={isImageRequired(getValues())}
              showImageRequired={showImageRequired}
            />

            {isUK() && (
              <>
                <FlexView className="margin-top-small">
                  <div className="bold">Is the UAS home built?</div>
                  <HomeBuiltPopover />
                </FlexView>
                <RadioGroup
                  onChange={isHomeBuiltChange}
                  value={homeBuiltWatch ? 'true' : 'false'}>
                  <div>
                    <FormControlLabel
                      label="Yes"
                      value={'true'}
                      control={<Radio color="primary" />}
                    />
                    <FormControlLabel
                      label="No"
                      value={'false'}
                      control={<Radio color="primary" />}
                    />
                  </div>
                </RadioGroup>
              </>
            )}
          </React.Fragment>
        )}

        {!isDroneForm() && (
          <React.Fragment>
            <TextField
              variant="outlined"
              label="Item description"
              name="description"
              inputRef={registerEquipment({ required: true })}
              helperText={
                errorsEquipment.description
                  ? 'Item description is required'
                  : 'Describe your item, for example, Zenmuse X5S Camera'
              }
              error={!!errorsEquipment.description}
            />
            <TextField
              className="margin-top-basic"
              variant="outlined"
              label="Serial"
              name="serial"
              inputRef={registerEquipment}
              helperText={errorsEquipment.serial && 'Serial is required'}
              error={!!errorsEquipment.serial}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    {' '}
                    <OptionalTag />{' '}
                  </InputAdornment>
                ),
              }}
            />
            <Grid className="margin-top-basic" container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  label="Value"
                  type="number"
                  name="value"
                  disabled={
                    isPolicyManagementMode() &&
                    props.editMode &&
                    props.editingInsuredHull
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {FirebaseService.getValue(
                          FirebaseConfigKey.CURRENCY_SYMBOL
                        )}
                      </InputAdornment>
                    ),
                  }}
                  inputRef={registerEquipment({
                    required: true,
                    min: FirebaseService.getNumber(
                      FirebaseConfigKey.MINIMUN_EQUIPMENT_VALUE
                    ),
                    max: maximumEquipmentValue,
                  })}
                  helperText={
                    errorsEquipment.value &&
                    `Value must be between ${FirebaseService.getValue(
                      FirebaseConfigKey.CURRENCY_SYMBOL
                    )}${FirebaseService.getNumber(
                      FirebaseConfigKey.MINIMUN_EQUIPMENT_VALUE
                    )} - ${FirebaseService.getValue(
                      FirebaseConfigKey.CURRENCY_SYMBOL
                    )}${maximumEquipmentValue.toLocaleString()}`
                  }
                  error={!!errorsEquipment.value}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  disabled={isPolicyManagementMode() && props.editMode}
                  variant="outlined"
                  label="Quantity"
                  type="number"
                  name="quantity"
                  inputRef={registerEquipment({
                    required: true,
                    validate: (x) => x > 0,
                  })}
                  helperText={
                    errorsEquipment.quantity && 'Quantity is required'
                  }
                  error={!!errorsEquipment.quantity}
                />
              </Grid>
            </Grid>
            {!!errorsEquipment.value && (
              <RequesetSupportTag
                tag={'hull_equipment_value'}
                text={'For higher equipment value'}
              />
            )}
          </React.Fragment>
        )}

        <NewFillButton onClick={onSubmit}>
          {props.editMode ? 'Save' : `Add this to the insurance plan`}
        </NewFillButton>
        {FirebaseService.getValue(
          FirebaseConfigKey.SHOULD_DISPLAY_HULL_ESTIMATIONS
        ) && (
          <div className="text-small blue bold textcenter margin-bottom-basic margin-top-small">
            {billingPeriodPretty(insuranceType)} Premium -{' '}
            {(
              FirebaseService.getValue(
                insuranceType.startsWith(InsuranceBillingPeriod.MONTH)
                  ? FirebaseConfigKey.HULL_ESTIMATED_PREMIUM_FACTOR
                  : FirebaseConfigKey.HULL_ESTIMATED_PREMIUM_FACTOR_ANNUAL
              ) * 100
            ).toFixed(2)}
            %
            <span className="margin-left-6">
              Deductible -{' '}
              {(
                FirebaseService.getValue(
                  insuranceType.startsWith(InsuranceBillingPeriod.MONTH)
                    ? FirebaseConfigKey.HULL_ESTIMATED_DEDUCTIBLE_FACTOR
                    : FirebaseConfigKey.HULL_ESTIMATED_DEDUCTIBLE_FACTOR_ANNUAL
                ) * 100
              ).toFixed(0)}
              %
            </span>
          </div>
        )}
      </FlexView>
    </React.Fragment>
  )
}
